import moment from 'moment';

// Status and date wording shared by the announcement and notification cards on the Communication
// tab. Kept out of the components so both read the same way and can be unit-tested.

/** What a message is doing right now: showing, still queued, or done with. */
export type MessageStatus = 'active' | 'scheduled' | 'sent' | 'past';

export type Translate = (key: string) => string;

const DATE_FORMAT = 'DD-MM-YYYY';

export const formatDate = (date: Date | undefined): string => (date == null ? '' : moment(date).format(DATE_FORMAT));

/** An announcement shows between its start and end date (both inclusive, end date is a full day). */
export const announcementStatus = (startDate: Date | undefined, endDate: Date | undefined): MessageStatus => {
  const today = moment().startOf('day');

  if (startDate != null && moment(startDate).startOf('day').isAfter(today)) {
    return 'scheduled';
  }

  if (endDate != null && moment(endDate).endOf('day').isBefore(moment())) {
    return 'past';
  }

  return 'active';
};

/**
 * A notification is a one-off push unless it recurs, in which case it keeps firing until its
 * expiration date — that window is what makes it "live" rather than simply sent.
 */
export const notificationStatus = (scheduledDateTime: Date, expirationDate: Date | undefined): MessageStatus => {
  if (moment(scheduledDateTime).isAfter(moment())) {
    return 'scheduled';
  }

  if (expirationDate != null && moment(expirationDate).endOf('day').isAfter(moment())) {
    return 'active';
  }

  return 'sent';
};

export const relativeDayLabel = (date: Date, translate: Translate): string => {
  const days = moment(date).startOf('day').diff(moment().startOf('day'), 'days');

  if (days === 0) return translate('relative-today');
  if (days === 1) return translate('relative-tomorrow');
  if (days === -1) return translate('relative-yesterday');

  return days > 0
    ? translate('relative-in-days').replace('[x]', days.toString())
    : translate('relative-days-ago').replace('[x]', Math.abs(days).toString());
};

/** Single moment in time ("14-08-2026 · in 6 days"), used for one-off notifications. */
export const momentLabel = (date: Date, translate: Translate): string =>
  translate('message-moment').replace('[x]', formatDate(date)).replace('[y]', relativeDayLabel(date, translate));

/** "Until 12-08-2026 · ends in 4 days" and friends, depending on the status. */
export const windowLabel = (
  status: MessageStatus,
  startDate: Date | undefined,
  endDate: Date | undefined,
  translate: Translate,
): string => {
  if (status === 'active' && endDate != null) {
    return translate('message-window-active').replace('[x]', formatDate(endDate)).replace('[y]', relativeDayLabel(endDate, translate));
  }

  if (status === 'scheduled' && startDate != null) {
    // An open-ended message has no range to print, so it falls back to the single-moment wording
    // instead of leaving a dangling dash.
    if (endDate == null) {
      return momentLabel(startDate, translate);
    }

    return translate('message-window-scheduled')
      .replace('[x]', formatDate(startDate))
      .replace('[y]', formatDate(endDate))
      .replace('[z]', relativeDayLabel(startDate, translate));
  }

  if (startDate != null && endDate != null) {
    return translate('message-window-past').replace('[x]', formatDate(startDate)).replace('[y]', formatDate(endDate));
  }

  const singleDate = startDate ?? endDate;
  return singleDate != null ? momentLabel(singleDate, translate) : '';
};

/** How far an active message is through its display window, for the progress bar. */
export const progressPercentage = (startDate: Date | undefined, endDate: Date | undefined): number | undefined => {
  if (startDate == null || endDate == null) {
    return undefined;
  }

  const start = moment(startDate).startOf('day').valueOf();
  const end = moment(endDate).endOf('day').valueOf();

  if (end <= start) {
    return undefined;
  }

  const elapsed = ((Date.now() - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, elapsed));
};
